import React, { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { usePassword } from "../context/PasswordContext";

const SeriesProtectedRoute = React.memo(({ children }) => {
  const { seriesId } = useParams();
  const { checkSeriesAccess, loading, passwordModal, authorizedSeries } =
    usePassword();
  const [isChecking, setIsChecking] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();
  const hasCheckedRef = useRef(false);

  // Memoize the checkAccess function
  const checkAccess = useCallback(async () => {
    // Don't check if we've already checked or if data isn't loaded
    if (hasCheckedRef.current || loading || !authorizedSeries) {
      return;
    }

    setIsChecking(true);

    // Check if we already have valid authorization
    const existingAuth = authorizedSeries.find(
      (item) => item.seriesId === seriesId
    );

    // If we have existing auth, verify without showing modal
    const { isAuthorized, error } = await checkSeriesAccess(
      seriesId,
      location.pathname,
      !existingAuth // Only show modal if no existing auth
    );

    if (error) {
      navigate("/");
      return;
    }

    // If not authorized and modal was closed (cancelled), redirect to home
    if (!isAuthorized && !passwordModal.isOpen && !existingAuth) {
      navigate("/");
      return;
    }

    hasCheckedRef.current = true;
    setIsChecking(false);
  }, [
    seriesId,
    checkSeriesAccess,
    loading,
    navigate,
    location.pathname,
    authorizedSeries?.length,
    passwordModal.isOpen // Add this dependency
  ]);

  // Reset check flag when series changes
  useEffect(() => {
    hasCheckedRef.current = false;
    return () => {
      hasCheckedRef.current = false;
    };
  }, [seriesId]);

  // Run checkAccess when component mounts or dependencies change
  useEffect(() => {
    if (!passwordModal.isOpen) {
      checkAccess();
    }
  }, [checkAccess, passwordModal.isOpen]);

  if (loading || isChecking) {
    return <div>Loading...</div>;
  }

  return children;
});

SeriesProtectedRoute.displayName = "SeriesProtectedRoute";

export default SeriesProtectedRoute;
